import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination } from "antd";
import { useLocation } from "react-router";

import Filters from "./Filters";
import Table from "./Table";
import FilterUpdateConfirmModal from "./components/FilterUpdateConfirmModal";
import SaveScreenerModal from "./components/SaveScreenerModal";
import LoadingSpinner from "./components/LoadingSpinner";
import { useScreenerData, useScreenerList, useScreenerActions } from "./hooks";

// ────────────────────────────────────────────────────────────────────
// ScreenerTable — thin orchestrator that wires hooks + child components
// ────────────────────────────────────────────────────────────────────
const ScreenerTable = () => {
  const location = useLocation();

  // ── Data hook (filters, table rows, sort, pagination) ─────────────
  const {
    filters,
    setFilters,
    sortBy,
    setSortBy,
    page,
    setPage,
    tableData,
    loading,
    initialLoading,
    viewMode,
    setViewMode,
    notificationCmpIds,
    setNotificationCmpIds,
    hasActiveFilters,
    fetchTableData,
    resetData,
    changePage,
    updateRows,
    markInitialLoadDone,
  } = useScreenerData();

  // ── List hook (CRUD on saved screeners) ───────────────────────────
  const {
    screenerLists,
    screenerName,
    setScreenerName,
    selectedScreenerId,
    setSelectedScreenerId,
    isSaveModalOpen,
    setIsSaveModalOpen,
    saveLoading,
    fetchScreenerList,
    handleScreenerSelect,
    handleSaveFilters,
    handleDeleteScreener,
    detachScreener,
  } = useScreenerList({
    fetchTableData,
    setFilters,
    setNotificationCmpIds,
    resetData,
  });

  // ── Action hook (approve / reject / shortlist) ────────────────────
  const openSaveModal = useCallback(
    () => setIsSaveModalOpen(true),
    [setIsSaveModalOpen],
  );

  const {
    pendingAction,
    clearPendingAction,
    handleCompanyAction,
    executeCompanyAction,
  } = useScreenerActions({
    selectedScreenerId,
    updateRows,
    openSaveModal,
  });

  // ── Filter-update confirmation state ──────────────────────────────
  const [isFilterConfirmOpen, setIsFilterConfirmOpen] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(null);

  /**
   * Wraps fetchTableData to intercept "editing filters on a saved screener".
   * When called from Filters (no id), checks if screener is saved first.
   */
  const handleApplyFilters = useCallback(
    (filtersToApply, id, skipConfirmation = false, cmpIdsOverride = null) => {
      if (selectedScreenerId && !id && !skipConfirmation) {
        setPendingFilters(filtersToApply);
        setIsFilterConfirmOpen(true);
        return;
      }

      // If applying without a saved screener, clear the link
      if (!id) {
        setScreenerName("");
        setSelectedScreenerId(null);
        setNotificationCmpIds(null);
      }

      fetchTableData(filtersToApply, id ?? null, cmpIdsOverride);
    },
    [
      selectedScreenerId,
      fetchTableData,
      setScreenerName,
      setSelectedScreenerId,
      setNotificationCmpIds,
    ],
  );

  // ── Filter-confirm modal handlers ─────────────────────────────────
  const handleSaveAsNew = useCallback(() => {
    setIsFilterConfirmOpen(false);
    setFilters(pendingFilters);
    detachScreener(pendingFilters);
    setIsSaveModalOpen(true);
    setPendingFilters(null);
  }, [pendingFilters, setFilters, detachScreener, setIsSaveModalOpen]);

  const handleApplyWithoutSaving = useCallback(() => {
    setIsFilterConfirmOpen(false);
    const toApply = pendingFilters;
    setPendingFilters(null);
    detachScreener(toApply);
    fetchTableData(toApply);
  }, [pendingFilters, detachScreener, fetchTableData]);

  const handleCancelFilterUpdate = useCallback(() => {
    setIsFilterConfirmOpen(false);
    setPendingFilters(null);
  }, []);

  // ── Reset handler (used by Filters component) ─────────────────────
  const resetDataHandler = useCallback(() => {
    resetData();
    setScreenerName("Untitled");
    setSelectedScreenerId(null);
    setViewMode("filter");
  }, [resetData, setScreenerName, setSelectedScreenerId]);

  // ── Pagination ─────────────────────────────────────────────────────
  const onPageChange = useCallback(
    (newPage) => changePage(newPage, selectedScreenerId),
    [changePage, selectedScreenerId],
  );

  // ── Save modal handlers ────────────────────────────────────────────
  const onSaveClick = useCallback(
    () => setIsSaveModalOpen(true),
    [setIsSaveModalOpen],
  );

  const onSave = useCallback(() => {
    const filtersToSave = pendingFilters || filters;
    handleSaveFilters(
      filtersToSave,
      tableData,
      pendingAction,
      executeCompanyAction,
    );
    clearPendingAction();
    setPendingFilters(null);
  }, [
    pendingFilters,
    filters,
    tableData,
    pendingAction,
    handleSaveFilters,
    executeCompanyAction,
    clearPendingAction,
  ]);

  const onCancelSave = useCallback(() => {
    setIsSaveModalOpen(false);
    clearPendingAction();
  }, [setIsSaveModalOpen, clearPendingAction]);

  // ── Initial load / re-load on same-URL navigation (e.g. notification click) ──
  useEffect(() => {
    const init = async () => {
      if (location?.state?.id) {
        await fetchScreenerList(location.state.id, location.state.cmp_ids);
      } else {
        await fetchScreenerList();
      }
      markInitialLoadDone();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── Re-fetch on sort change ────────────────────────────────────────
  useEffect(() => {
    if (hasActiveFilters(filters) || selectedScreenerId) {
      fetchTableData({ ...filters }, selectedScreenerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  // ── Re-fetch on view mode change ──────────────────────────────────
  useEffect(() => {
    if (hasActiveFilters(filters) || selectedScreenerId) {
      setPage(1);
      fetchTableData({ ...filters, page: 1 }, selectedScreenerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // ── Memoised table props ──────────────────────────────────────────
  const tableProps = useMemo(
    () => ({
      totalCount: tableData.total_count,
      data: tableData.data,
      loading,
      filters,
      hasActiveFilters,
      onSaveClick,
      onCompanyAction: handleCompanyAction,
      screenerName,
      setScreenerName,
      screenerLists,
      selectedScreenerId,
      onScreenerSelect: handleScreenerSelect,
      onDeleteScreener: handleDeleteScreener,
      sortBy,
      setSortBy,
      viewMode,
      onViewModeChange: setViewMode,
    }),
    [
      tableData,
      loading,
      filters,
      hasActiveFilters,
      onSaveClick,
      handleCompanyAction,
      screenerName,
      setScreenerName,
      screenerLists,
      selectedScreenerId,
      handleScreenerSelect,
      handleDeleteScreener,
      sortBy,
      setSortBy,
      viewMode,
      setViewMode,
    ],
  );

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="pb-2">
      <Filters
        filters={filters}
        setFilters={setFilters}
        resetDataHandler={resetDataHandler}
        onApplyFilters={handleApplyFilters}
        setViewMode={setViewMode}
      />

      {initialLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Table {...tableProps} />
          <Pagination
            current={page}
            total={tableData.total_count}
            pageSize={20}
            onChange={onPageChange}
            className="flex justify-center"
          />
        </>
      )}

      {/* Modal: filter-update confirmation */}
      {isFilterConfirmOpen && (
        <FilterUpdateConfirmModal
          onCancel={handleCancelFilterUpdate}
          onApplyWithoutSaving={handleApplyWithoutSaving}
          onSaveAsNew={handleSaveAsNew}
        />
      )}

      {/* Modal: save screener */}
      {isSaveModalOpen && (
        <SaveScreenerModal
          screenerName={screenerName}
          onNameChange={setScreenerName}
          onSave={onSave}
          onCancel={onCancelSave}
          saveLoading={saveLoading}
        />
      )}
    </div>
  );
};

export default ScreenerTable;